import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import TcEventService from '../TcEventService';
import { useTranslation } from 'react-i18next';
import { getPersonnelRolesName } from 'shared/helpers';
import {
  ModalInfo,
  ExamineeStatusTabs,
  AlertNotice,
  Input,
  TestMaterials,
  Spinner,
} from 'shared/components/index';
import RoomsReviewDetails from './rooms-review-details/RoomsReviewDetails';
import { groupBy } from 'lodash';
import Parser from 'html-react-parser';
import './TcEventCheckout.scss';
import RostersService from 'features/rosters/RostersService';

const EventCheckoutModal = (props) => {
  const { t } = useTranslation();
  const {
    tcEvent,
    totalSubmittedIrreg,
    eventStaffCounts,
    roomsOnlineFlag,
    actions,
    nrOfRooms,
    isNowManagedFlag,
  } = props;
  const [loadingData, setLoadingData] = useState(false);
  const [reviewFlag, setReviewFlag] = useState(false);
  const [scratchPaperReviewFlag, setScratchPaperReviewFlag] = useState(false);
  const [reviewErrorFlag, setReviewErrorFlag] = useState(false);
  const [examinees, setExaminees] = useState([]);
  const [examineesStatus, setExamineesStatus] = useState({});
  const [roomListReview, setRoomsStatus] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [answerDocs, setAnswerDocs] = useState([]);
  const personnelRolesName = getPersonnelRolesName();
  const [activeTab, setActiveTab] = useState('unknown');
  const [showRemoveStudentsWarningModal, setShowRemoveStudentsWarningModal] = useState(false);
  const [studentsToRemove, setStudentsToRemove] = useState([]);
  const [onlineExamineesCount, setOnlineExamineesCount] = useState(0);

  const mapRooms = (rooms) => {
    return Object.values(groupBy(rooms, 'locationName'));
  };

  const fetchData = async () => {
    setLoadingData(true);
    await actions.getTcEventStaff(tcEvent.id);
    await actions.getTcEventIrregularities(tcEvent.id);
    const roomsReview = await TcEventService.getTcEventRoomsReviewStatus(tcEvent.id);
    const examStatus = await TcEventService.getTcEventExamineesSummary(tcEvent.id);
    const examList = await TcEventService.getTcEventExamineesStatus(tcEvent.id, activeTab);
    const materialsSummary = await TcEventService.getTcEventMaterialSummary(tcEvent.id);
    const answers = await TcEventService.getTcEventAnswerDocs(tcEvent.id);

    setRoomsStatus(mapRooms(roomsReview.data));
    setExamineesStatus(examStatus.data.length ? examStatus.data[0] : {});
    setExaminees(examList.data);
    setMaterials(materialsSummary.data);
    setAnswerDocs(answers.data.length ? answers.data[0] : {});
    setLoadingData(false);
  };

  useEffect(() => {
    if (loadingData) return;
    Array.from(document.querySelectorAll('[data-bs-toggle="popover"]')).forEach((el) =>
      bootstrap.Popover.getOrCreateInstance(el).enable()
    );
  }, [loadingData]);

  useEffect(() => {
    if (!props.modalOpen) {
      setReviewErrorFlag(false);
      setReviewFlag(false);
    } else {
      fetchData();
    }
  }, [props.modalOpen]);

  useEffect(() => {
    (async () => {
      fetchData();
    })();
  }, [activeTab]);

  useEffect(() => {
    (async () => {
      const absentsStudents = await TcEventService.getTcEventExamineesStatus(tcEvent.id, 'absent');
      const deniedStudents = await TcEventService.getTcEventExamineesStatus(tcEvent.id, 'denied');
      if (absentsStudents?.data && deniedStudents?.data) {
        const absentsStudentsIds = absentsStudents.data.map((a) => a.regId);
        const deniedStudentsIds = deniedStudents.data.map((d) => d.regId);
        setStudentsToRemove([...absentsStudentsIds, ...deniedStudentsIds]);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchOnlineExamineesCount = async () => {
      const hasOpenLocationEvent = tcEvent.locationEvents.some(
        (location) => location.status === 'OPEN'
      );
      if (hasOpenLocationEvent) {
        const locationEventId = tcEvent.locationEvents.find(
          (location) => location.status === 'OPEN'
        )?.id;
        try {
          const response = await RostersService.getLocationEventExamineeCount(locationEventId);
          if (response && response.length > 0) {
            const onlineExaminees = response.filter(
              (examinee) => examinee.onlineFlag === true && examinee.regsAssigned > 0
            ).length;
            setOnlineExamineesCount(onlineExaminees);
          } else {
            console.log('Error fetching online examinees count: No data returned');
          }
        } catch (error) {
          console.log('Error fetching online examinees count:', error);
        }
      } else {
        setOnlineExamineesCount(0);
        console.log('No open location events found');
      }
    };
    fetchOnlineExamineesCount();
  }, [tcEvent]);

  const submitEventCheckout = async () => {
    if (reviewFlag && (scratchPaperReviewFlag || onlineExamineesCount === 0)) {
      if (isNowManagedFlag && studentsToRemove.length) {
        props.toggleModal();
        setShowRemoveStudentsWarningModal(true);
        return;
      }
      await actions.reviewEventCheckout(tcEvent);
      props.toggleModal();
    } else {
      setReviewErrorFlag(true);
    }
  };
  const submitRemoveStudents = async () => {
    setShowRemoveStudentsWarningModal(false);
    const data = { regIDs: studentsToRemove };
    await TcEventService.unassignStudents(data);
    await actions.reviewEventCheckout(tcEvent);
  };

  const onChangeCheckbox = (e) => {
    console.debug('onChangeCheckbox');
    if (e.target.id === 'reviewCheckout') {
      setReviewFlag(e.target.checked);
      if (e.target.checked) {
        tcEvent.eventCheckoutFlag = true;
        if (scratchPaperReviewFlag) {
          tcEvent.scratchPaperFlag = true;
        } else {
          tcEvent.scratchPaperFlag = false;
        }
      } else {
        tcEvent.eventCheckoutFlag = false;
        tcEvent.scratchPaperFlag = false;
      }
    } else if (e.target.id === 'reviewCheckout1') {
      setScratchPaperReviewFlag(e.target.checked);
      if (e.target.checked) {
        tcEvent.scratchPaperFlag = true;
        if (reviewFlag) {
          tcEvent.eventCheckoutFlag = true;
        }
      } else {
        tcEvent.scratchPaperFlag = false;
      }
    }
    setReviewErrorFlag(false);
  };

  return (
    <>
      <ModalInfo
        containerClass="event-checkout-modal"
        modalIsOpen={props.modalOpen}
        closeModal={props.toggleModal}
        headerTitle={t('tcEventDashboard.checkout.title')}
        onSubmit={submitEventCheckout}
        saveLabel={t('common.labels.submit')}
        cancelLabel={t('common.labels.back')}
        loadingButton={loadingData}
        showCancel
        fullHeightModal
        headerBack>
        {loadingData && <Spinner hideText noHeight />}
        <div className={`${loadingData ? 'loading-data' : ''}`}>
          <ExamineeStatusTabs
            statusSummary={examineesStatus}
            examinees={examinees}
            setActiveTab={setActiveTab}
            tabs={['unknown', 'absent', 'deniedAdmission', 'tested']}
            isNowManagedFlag={isNowManagedFlag}
            isEventCheckoutFlag
          />
          <div className="border-delimiter"></div>
          <RoomsReviewDetails
            roomsList={roomListReview}
            mappedRoomTiming={props.mappedRoomTiming}
            programCode={tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code}
          />
          <div className="border-delimiter"></div>
          {materials.length > 0 && (
            <>
              <TestMaterials materials={materials} />
              {
                <div className="answers-container">
                  <div className="answer-list-item">
                    <div>{t('tcEventDashboard.checkout.scored')}</div>
                    <div>{answerDocs.answerDocScored ?? 0}</div>
                  </div>
                  <div className="answer-list-item">
                    <div>{t('tcEventDashboard.checkout.voided')}</div>
                    <div>{answerDocs.answerDocVoided ?? 0}</div>
                  </div>
                  <div className="answer-list-item">
                    <div>{t('tcEventDashboard.checkout.replaced')}</div>
                    <div>{answerDocs.answerDocReplaced ?? 0}</div>
                  </div>
                </div>
              }
              <div className="border-delimiter"></div>
            </>
          )}
          <div>
            <div className="h4">{t('tcEventDashboard.checkout.irregularities')}</div>
            {totalSubmittedIrreg ? (
              <AlertNotice
                message={t('tcEventDashboard.checkout.irregularitiesWarning', {
                  total: totalSubmittedIrreg,
                })}
                type="warning"
              />
            ) : (
              <AlertNotice
                message={t('tcEventDashboard.checkout.noIrregularities')}
                type="success"
                noBorder
              />
            )}
          </div>
          <div className="border-delimiter"></div>
          <div className="staff-section">
            <div className="h4">{t('tcEventDashboard.checkout.staff')}</div>
            <AlertNotice message={t('tcEventDashboard.checkout.staffInfoMessage')} />
            {(tcEvent.unlimitedRovingProctorsFlag || nrOfRooms >= 8) && (
              <div className="mb-1 info-row">
                <span className="fw-bold">{personnelRolesName.PROCTOR_ROVING} </span>
                <span>{eventStaffCounts.rovingProctors}</span>
              </div>
            )}
            {roomsOnlineFlag && (
              <div className="mb-1 info-row">
                <span className="fw-bold">{personnelRolesName.TECH_COORDINATOR} </span>
                <span>{eventStaffCounts.techCoordinator}</span>
              </div>
            )}
            <div className="mb-1 info-row">
              <span className="fw-bold">{personnelRolesName.ROOM_SUPERVISOR} </span>
              <span>{eventStaffCounts.roomSupervisors}</span>
            </div>
            <div className="mb-1 info-row">
              <span className="fw-bold">{personnelRolesName.PROCTOR} </span>
              <span>{eventStaffCounts.proctors}</span>
            </div>
          </div>
          <div className="border-delimiter"></div>
          <div>
            {reviewErrorFlag && (
              <AlertNotice
                message={
                  onlineExamineesCount > 0
                    ? t('tcEventDashboard.checkout.scratchPaperError')
                    : t('tcEventDashboard.checkout.errorReview')
                }
                type="error"
              />
            )}
            {onlineExamineesCount > 0 && (
              <Input
                containerClassName=""
                type="checkbox"
                name="reviewCheckout1"
                id="reviewCheckout1"
                checked={scratchPaperReviewFlag}
                onChange={onChangeCheckbox}
                errors={reviewErrorFlag ? [''] : null}>
                <label htmlFor="reviewCheckout1">
                  {t('tcEventDashboard.checkout.scratchPaperMessage')}
                </label>
              </Input>
            )}
            <Input
              containerClassName=""
              type="checkbox"
              name="reviewCheckout"
              id="reviewCheckout"
              checked={reviewFlag}
              onChange={onChangeCheckbox}
              errors={reviewErrorFlag ? [''] : null}>
              <label htmlFor="reviewCheckout">{t('tcEventDashboard.checkout.reviewMessage')}</label>
            </Input>
          </div>
        </div>
      </ModalInfo>
      <ModalInfo
        containerClass="confirm-student-removal-container"
        onSubmit={submitRemoveStudents}
        modalIsOpen={showRemoveStudentsWarningModal}
        onCancel={() => setShowRemoveStudentsWarningModal(false)}
        className="confirm-student-removal-modal"
        saveLabel="Remove students & Submit"
        cancelLabel="Cancel"
        showCancel>
        <h5>{t('tcEventDashboard.checkout.studentRemovalQuestion')}</h5>
        <div>{Parser(t('tcEventDashboard.checkout.studentRemovalText'))}</div>
      </ModalInfo>
    </>
  );
};

export default EventCheckoutModal;

EventCheckoutModal.propTypes = {};
